import { Menu, X } from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import Logo from "@/assets/logo.png";
import type { NavLink } from "@/types";

const navLinks: readonly NavLink[] = [
	{ href: "#hero", label: "", id: "hero" }, // Dummy für Home/Top
	{ href: "#about", label: "#über uns", id: "about" },
	{ href: "#contact", label: "#kontakt", id: "contact" },
	{ href: "#works", label: "#works", id: "works" },
] as const;

const Navigation: React.FC = () => {
	const [isScrolled, setIsScrolled] = useState<boolean>(false);
	const [isMobileMenuOpen, setIsMobileMenuOpen] = useState<boolean>(false);
	const [activeSection, setActiveSection] = useState<string>("hero");
	const location = useLocation();
	const navigate = useNavigate();
	const isHome = location.pathname === "/";

	// Scroll listener für Hintergrund
	useEffect(() => {
		const handleScroll = (): void => {
			setIsScrolled(window.scrollY > 30);
			// Wenn ganz oben, hero aktiv setzen
			if (window.scrollY < 100 && isHome) {
				setActiveSection("hero");
			}
		};

		window.addEventListener("scroll", handleScroll, { passive: true });
		return () => window.removeEventListener("scroll", handleScroll);
	}, [isHome]);

	// Intersection Observer für aktive Section
	useEffect(() => {
		if (!isHome) {
			setActiveSection("");
			return;
		}

		const observer = new IntersectionObserver(
			(entries) => {
				entries.forEach((entry) => {
					if (entry.isIntersecting) {
						setActiveSection(entry.target.id);
					}
				});
			},
			{ threshold: 0.2, rootMargin: "-25% 0px -25% 0px" },
		);

		navLinks.forEach((link) => {
			const element = document.getElementById(link.id);
			if (element) observer.observe(element);
		});

		return () => observer.disconnect();
	}, [isHome]);

	useEffect(() => {
		setIsMobileMenuOpen(false);
	}, []);

	const handleNavClick = useCallback(
		(e: React.MouseEvent<HTMLAnchorElement>, href: string): void => {
			e.preventDefault();

			// Mobile Menu schließen
			setIsMobileMenuOpen(false);

			if (!isHome) {
				// Nicht auf Startseite - navigiere mit Hash
				navigate(`/${href}`);
			} else {
				// Bereits auf Startseite - scroll direkt mit Offset
				const element = document.querySelector(href);
				if (element) {
					const navHeight = 84; 
					const elementPosition = element.getBoundingClientRect().top;
					const offsetPosition = elementPosition + window.pageYOffset - navHeight;

					window.scrollTo({
						top: offsetPosition,
						behavior: "smooth"
					});
				}
			}
		},
		[isHome, navigate],
	);

	const isActive = (id: string): boolean => activeSection === id;

	return (
		<nav
			className={`fixed top-0 left-0 right-0 z-40 transition-all duration-300 ${
				isScrolled || isMobileMenuOpen ? "bg-black/95 backdrop-blur-sm py-4" : "bg-transparent py-6"
			}`}
		>
			<div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
				<div className="flex items-center justify-between">
					{/* Logo / Brand */}
					<Link
						to="/"
						className="flex items-center group relative"
						onClick={() => {
							window.scrollTo({ top: 0, behavior: "smooth" });
						}}
					>
						<div className="bg-black w-12 h-12 md:w-16 md:h-16 rounded-full border border-white/10 shadow-2xl flex items-center justify-center overflow-hidden transition-all duration-300 group-hover:scale-105">
							<img
								src={Logo}
								alt="WILDFÖRSTER"
								className="w-[85%] h-[85%] object-contain"
							/>
						</div>
					</Link>

					{/* Desktop Navigation */}
					<div className="hidden md:flex items-center space-x-8">
						{navLinks
							.filter((link) => link.label)
							.map((link) => (
								<a
									key={link.id}
									href={link.href}
									onClick={(e) => handleNavClick(e, link.href)}
									className={`nav-link ${isActive(link.id) ? "active" : ""}`}
								>
									{link.label}
								</a>
							))}
					</div>

					{/* Social Icons - Desktop */}
					<div className="hidden md:flex items-center space-x-4">
						<SocialLink
							href="https://m.facebook.com/Daniel-Wildf%C3%B6rster-113290246971839/"
							label="Facebook"
						>
							<FacebookIcon />
						</SocialLink>
						<SocialLink
							href="https://www.instagram.com/wildforsterdaniel/?hl=de"
							label="Instagram"
						>
							<InstagramIcon />
						</SocialLink>
						<SocialLink
							href="https://www.tiktok.com/@wildfoerster13?"
							label="TikTok"
						>
							<TikTokIcon />
						</SocialLink>
					</div>

					{/* Mobile Menu Button */}
					<button
						className="md:hidden text-white p-2"
						onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
						aria-label="Menü öffnen"
					>
						{isMobileMenuOpen ? (
							<X className="w-6 h-6" />
						) : (
							<Menu className="w-6 h-6" />
						)}
					</button>
				</div>

				{/* Mobile Menu */}
				{isMobileMenuOpen && (
					<div className="md:hidden mt-4 pb-4 border-t border-white/20 pt-4">
						<div className="flex flex-col space-y-4">
							{navLinks
								.filter((link) => link.label)
								.map((link) => (
									<a
										key={link.id}
										href={link.href}
										onClick={(e) => handleNavClick(e, link.href)}
										className={`text-white/70 hover:text-white transition-colors text-lg ${isActive(link.id) ? "text-white font-bold" : ""}`}
									>
										{link.label}
									</a>
								))}
							<div className="flex space-x-6 pt-4 border-t border-white/20">
								<SocialLink
									href="https://m.facebook.com/Daniel-Wildf%C3%B6rster-113290246971839/"
									label="Facebook"
								>
									<FacebookIcon />
								</SocialLink>
								<SocialLink
									href="https://www.instagram.com/wildforsterdaniel/?hl=de"
									label="Instagram"
								>
									<InstagramIcon />
								</SocialLink>
								<SocialLink
									href="https://www.tiktok.com/@wildfoerster13?"
									label="TikTok"
								>
									<TikTokIcon />
								</SocialLink>
							</div>
						</div>
					</div>
				)}
			</div>
		</nav>
	);
};

// Sub-components
interface SocialLinkProps {
	readonly href: string;
	readonly label: string;
	readonly children: React.ReactNode;
}

const SocialLink: React.FC<SocialLinkProps> = ({ href, label, children }) => (
	<a
		href={href}
		target="_blank"
		rel="noopener noreferrer"
		className="text-white/70 hover:text-white transition-colors"
		aria-label={label}
	>
		{children}
	</a>
);

const FacebookIcon: React.FC = () => (
	<svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
		<path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
	</svg>
);

const InstagramIcon: React.FC = () => (
	<svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
		<path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z" />
	</svg>
);

const TikTokIcon: React.FC = () => (
	<svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
		<path d="M12.525.02c1.31-.02 2.61-.01 3.91-.02.08 1.53.63 3.09 1.75 4.17 1.12 1.11 2.7 1.62 4.24 1.79v4.03c-1.44-.05-2.89-.35-4.2-.97-.57-.26-1.1-.59-1.62-.93-.01 2.92.01 5.84-.02 8.75-.08 1.4-.54 2.79-1.35 3.94-1.31 1.92-3.58 3.17-5.91 3.21-1.43.08-2.86-.31-4.08-1.03-2.02-1.19-3.44-3.37-3.65-5.71-.02-.5-.03-1-.01-1.49.18-1.9 1.12-3.72 2.58-4.96 1.66-1.44 3.98-2.13 6.15-1.72.02 1.48-.04 2.96-.04 4.44-.99-.32-2.15-.23-3.02.37-.63.41-1.11 1.04-1.36 1.75-.21.51-.15 1.07-.14 1.61.24 1.64 1.82 3.02 3.5 2.87 1.12-.01 2.19-.66 2.77-1.61.19-.33.4-.67.41-1.06.1-1.79.06-3.57.07-5.36.01-4.03-.01-8.05.02-12.07z" />
	</svg>
);

export default Navigation;
